import firebase from 'firebase/app';
import 'firebase/firestore';
import 'firebase/database';
import _ from 'lodash';
import format from 'date-fns/format';
import { stat } from 'fs';

// transform doc to feed (DTO)
// @see src\components\sparks\nudges.vue
const uiAssembler = {
  // @se src\assets\images\dimension
  getImageName(targetItem: any) {
    const resolution = "@2x";
    const pictureNamesMap = {
      "Happiness": "Happiness" + resolution,
      "Personal Growth": "Personal-Growth" + resolution,
      "Recognition": "Recognition" + resolution,
      "Relationship with Colleagues": "Rel.Colleagues" + resolution,
      "Satisfaction": "Satisfaction" + resolution,
      "Ambassadorship": "Ambassadorship" + resolution,
      "Feedback": "Feedback" + resolution,
      "Relationship with Manager": "Rel.Manager" + resolution,
      "Wellness": "Wellness" + resolution,
      "Company Alignment": "Company-Alignment" + resolution,
      "Introspection": "Introspection" + resolution,
      // "Performance": "Performance" + resolution,
      // "Here Point Coins": "Here-Point-Coins" + resolution,
      // "Here Point Gems": "Here-Point-Gems" + resolution,
      // "Welcome": "Welcome" + resolution
    };

    if (pictureNamesMap.hasOwnProperty(targetItem.dimension)) {
      if (pictureNamesMap[targetItem.dimension]) {
        return pictureNamesMap[targetItem.dimension];
      }
    }

    // prevent error
    // targetItem.icon, targetItem.dimension
    return "ic_happy@2x";
  },
  createFeedDto(doc: any) {
    const data = doc.data();
    const targetItem = data.firstItem;
    const feed = {
      id: doc.id,
      targetItemId: targetItem.id,
      targetItemEmail: targetItem.email,
      dimension: targetItem.dimension,
      imageName: this.getImageName(targetItem),
      icon: targetItem.icon,
      label: targetItem.nudge,
      recipient: targetItem.recipient,
      dateAndTime: targetItem.createdDate,
      status: "white",
      duplicateValue: data.totalItems > 1,
      showDuplicateValue: false,
      child: [],
      loaded: false,
      totalItems: data.totalItems,
      variables: targetItem.variables,
    };
    this.assignBogusChildren(feed);
    feed.status = this.getStatusCls({ ...targetItem, totalItems: feed.totalItems });

    return feed;
  },
  assignBogusChildren(feed: any) {
    // set bogus children for ui display with grouping sets
    const isGroupingSets = feed.totalItems > 1;
    if (isGroupingSets) {
      // @see https://stackoverflow.com/questions/105034/how-to-create-a-guid-uuid
      const generateUUID = () => {
        var d = new Date().getTime();//Timestamp
        var d2 = ((typeof performance !== 'undefined') && performance.now && (performance.now() * 1000)) || 0;//Time in microseconds since page-load or 0 if unsupported
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
          var r = Math.random() * 16;//random number between 0 and 16
          if (d > 0) {//Use timestamp until depleted
            r = (d + r) % 16 | 0;
            d = Math.floor(d / 16);
          } else {//Use microseconds since page-load if supported
            r = (d2 + r) % 16 | 0;
            d2 = Math.floor(d2 / 16);
          }
          return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
        });
      }
      for (let i = 0; i < 3; i++) {
        const child = {
          id: `-${generateUUID()}`,
          imageName: this.getImageName(feed),
        }
        feed.child.push(child);
      }
    }
  },
  createSubFeedDto(doc: any) {
    const targetItem = doc.data();
    const feed = {
      id: doc.id,
      email: targetItem.email,
      dimension: targetItem.dimension,
      imageName: this.getImageName(targetItem),
      icon: targetItem.icon,
      label: targetItem.nudge,
      recipient: targetItem.recipient,
      dateAndTime: targetItem.createdDate,
      status: "white",
      duplicateValue: false,
      showDuplicateValue: false,
      child: [],
      totalItems: 0,
      variables: targetItem.variables,
    };
    feed.status = this.getStatusCls({ ...targetItem, totalItems: feed.totalItems });
    return feed;
  },
  getStatusCls(origData: any): string {
    // @see https://trello.com/c/srMuQpiF/1302-sparks-as-a-company-i-want-to-be-able-to-view-the-nudges-that-are-sent-so-that-i-have-a-better-sense-of-the-impact-of-the-person
    // colors: white, red and green
    const defaultStatusCls = "white";
    let statusCls = defaultStatusCls;

    let clsMap = {};
    clsMap[`manager.response.great`] = "green";
    clsMap[`manager.response.not good`] = "red";
    clsMap[`manager.response.okay`] = defaultStatusCls;   
    clsMap[`manager.change.improved`] = "green";
    clsMap[`manager.change.same`] = defaultStatusCls;
    clsMap[`manager.change.declined`] = "red";

    clsMap[`employee.response.1`] = "green";
    clsMap[`employee.response.0`] = "red";  
    clsMap[`employee.change.improved`] = "green";
    clsMap[`employee.change.same`] = defaultStatusCls;
    clsMap[`employee.change.declined`] = "red";

    // grouping sets and row colours
    const { eventTarget, eventTrigger, eventState } = origData;
    let clsKey = `${eventTarget}.${eventTrigger}.${eventState}`;

    if (eventTarget == "employee" && eventTrigger == "response") {
      if (origData.hasOwnProperty("variables")) {
        const weight = origData.variables["{{weight}}"];
        clsKey = `${eventTarget}.${eventTrigger}.${weight}`;
      }
    }

    if (clsMap.hasOwnProperty(clsKey)) {
      statusCls = clsMap[clsKey];
    }

    return statusCls;
  },
  
  // @see https://stackoverflow.com/questions/3177836/how-to-format-time-since-xxx-e-g-4-minutes-ago-similar-to-stack-exchange-site
  timeAgo(time) {
    switch (typeof time) {
      case 'number':
        break;
      case 'string':
        time = +new Date(time);
        break;
      case 'object':
        if (time.constructor === Date) time = time.getTime();
        break;
      default:
        time = +new Date();
    }
    const time_formats = [
      [60, 'seconds', 1], // 60
      [120, '1 minute ago', '1 minute from now'], // 60*2
      [3600, 'minutes', 60], // 60*60, 60
      [7200, '1 hour ago', '1 hour from now'], // 60*60*2
      [86400, 'hours', 3600], // 60*60*24, 60*60
      [172800, 'Yesterday', 'Tomorrow'], // 60*60*24*2
      [604800, 'days', 86400], // 60*60*24*7, 60*60*24
      [1209600, 'Last week', 'Next week'], // 60*60*24*7*4*2
      [2419200, 'weeks', 604800], // 60*60*24*7*4, 60*60*24*7
      [4838400, 'Last month', 'Next month'], // 60*60*24*7*4*2
      [29030400, 'months', 2419200], // 60*60*24*7*4*12, 60*60*24*7*4
      [58060800, 'Last year', 'Next year'], // 60*60*24*7*4*12*2
      [2903040000, 'years', 29030400], // 60*60*24*7*4*12*100, 60*60*24*7*4*12
      [5806080000, 'Last century', 'Next century'], // 60*60*24*7*4*12*100*2
      [58060800000, 'centuries', 2903040000] // 60*60*24*7*4*12*100*20, 60*60*24*7*4*12*100
    ];
    let seconds = (+new Date() - time) / 1000,
      token = 'ago',
      list_choice = 1;
  
    if (seconds == 0) {
      return 'Just now'
    }
    if (seconds < 0) {
      seconds = Math.abs(seconds);
      token = 'from now';
      list_choice = 2;
    }
    let i = 0,
      format;
    while (format = time_formats[i++])
      if (seconds < format[0]) {
        if (typeof format[2] == 'string')
          return format[list_choice];
        else
          return Math.floor(seconds / format[2]) + ' ' + format[1] + ' ' + token;
      }
    return time;
  }
}

export default {
  namespaced: true,
  state: {
    initialized: false,
    latestDate: null,
    nextLatestDate: null,
    hasMore: false,
    loading: false,
    loadingChildren: false,
    feeds: [],
    categoriesMap: {},
  },
  mutations: {
    initialized(state: any, result: any) {
      state.initialized = result;
    },
    categoriesMap(state: any, result: any) {
      state.categoriesMap = result;
    },
    latestDate(state: any, result: any) {
      state.latestDate = result;
    },
    nextLatestDate(state: any, result: any) {
      state.nextLatestDate = result;
    },
    hasMore(state: any, result: any) {
      state.hasMore = result;
    },
    loading(state: any, result: any) {
      state.loading = result;
    },
    loadingChildren(state: any, result: any) {
      state.loadingChildren = result;
    },
    addFeeds(state: any, feeds: any) {
      for (const i in feeds) {
        const feed = feeds[i];
        // prevent duplciate feed.id from hot code push
        if (!_.find(state.feeds, { id: feed.id })) {
          state.feeds.push(feed);
        }
      }
    },
  },
  actions: {
    async getFeeds({ commit, rootState, dispatch, state }: any, latestDate: any) {
      const initialized = state.initialized;
      if (!latestDate) {
        latestDate = format(new Date(), "YYYY-MM-DD HH:mm:ss");
      }
      commit("latestDate", latestDate);

      // set categories for dimension info
      /*
      if (!initialized) {
        firebase.database().ref('category').once('value', snapshot => {
          const categoriesMap = {};
          snapshot.forEach((doc) => {
              const value = doc.val();
              value._key = doc.key;
              categoriesMap[value.category] = value;
          }); 
          commit("categoriesMap", categoriesMap);
        });
      }
      //*/

      const DEFAULT_TOTAL_DATES = 1;
      const DEFAULT_TOTAL_DATES_FIRSTTIME = 5;

      const diffDates: string[] = [];
      const maxDiffDates = initialized ? 
        DEFAULT_TOTAL_DATES : 
        DEFAULT_TOTAL_DATES_FIRSTTIME;

      const user = rootState.authen.user;
      const limit = 20;

      // const path = `sparknotification_event_nudges/${user.company}/nudges`;
      const query = firebase.firestore()
        .collectionGroup("nudges")
        .where("companyId", "==", user.company)
        .orderBy("createdDate", "desc")
        .startAfter(latestDate)
        .limit(limit);

      commit("loading", true);
      await query
        .get().then((querySnapshot) => {
          const docs: any[] = [];
          querySnapshot.forEach(async (doc) => {
            docs.push(doc);
          });

          const feeds: any[] = [];
          let nextLatestDates: any[] = [];
          let hasMore = false;
          for (const i in docs) {
            const createdDate = String(docs[i].data().createdDate);
            if (!createdDate) continue;

            const $createdDate = createdDate.substring(0, 10);
            if (diffDates.indexOf($createdDate) === -1) {
              if (diffDates.length < maxDiffDates) {
                diffDates.push($createdDate);
                nextLatestDates.push(createdDate);
                feeds.push(uiAssembler.createFeedDto(docs[i]));
              } else {
                hasMore = true;
              }
            } else {
              diffDates.push($createdDate);
              nextLatestDates.push(createdDate);
              feeds.push(uiAssembler.createFeedDto(docs[i]));
            }
          }

          commit("addFeeds", feeds);

          nextLatestDates.sort();
          if (nextLatestDates.length) {
            commit("nextLatestDate", nextLatestDates[0]);
          }

          commit("hasMore", hasMore);
          commit("loading", false);
        });

      commit("initialized", true);
    },
    async showFeedSet({ commit, rootState, dispatch, state }: any, feed: any) {
      const hasChild = feed.totalItems > feed.child.length;
      if (hasChild && (feed.loaded === false)) {
        feed.child = [];
        const user = rootState.authen.user;
        const path = `sparknotification_event_nudges/${user.company}/nudges`;
        const ref = firebase.firestore()
          .collection(`${path}/${feed.id}/notifications`);
        commit("loadingChildren", true);
        await ref.onSnapshot((querySnapshot) => {
          // console.group("notifications");
          if (!_.isArray(feed.child)) {
            feed.child = [];
          }
          // console.log(feed, "feed");
          querySnapshot.forEach((doc) => {
            const child = uiAssembler.createSubFeedDto(doc);
            // Don't known id because of bactch insert documents
            // @todo check by id NOT email. 
            const found = feed.targetItemEmail == child.email;
            !found && feed.child.push(child);
          });

          feed.child = _.sortBy(feed.child, [(o) => { return o.dateAndTime; }]);
          feed.child.reverse();

          feed.loaded = true;
          commit("loadingChildren", false);
          // console.groupEnd();
        });
      }

      feed.showDuplicateValue = true;
    },
    async hideFeedSet({ commit, rootState, dispatch, state }: any, feed: any) {
      feed.showDuplicateValue = false;
    },
    async hasMore({ commit, rootState, dispatch, state }: any, value: any) {

    },
  },
  getters: {
    latestDate: (state: any) => {
      return state.latestDate;
    },
    nextLatestDate: (state: any) => {
      return state.nextLatestDate;
    },
    hasMore: (state: any) => {
      return state.hasMore;
    },
    loading: (state: any) => {
      return state.loading;
    },
    loadingChildren: (state: any) => {
      return state.loadingChildren;
    },
    feeds: (state: any) => {
      return state.feeds;
    },
    categoriesMap: (state: any) => {
      return state.categoriesMap;
    },
  }
}